// ProtectedRoute.jsx - Source de vérité = backend (activeRole / requiresRoleSelection)
import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import RoleSwitcher from '../components/RoleSwitcher';

const PUBLIC_ROUTES = [
    '/accueil',
    '/login',
    '/inscription',
    '/forgot-password',
    '/reset-password',
    '/demo',
    '/confidentialite',
    '/mentions-legales',
];

function isPublicRoute(pathname) {
    return PUBLIC_ROUTES.some(route => pathname === route || pathname.startsWith(route + '/'));
}

const ProtectedRoute = ({ roleConfig }) => {
    const {
        token,
        user,
        loading,
        logout,
        activeRole,
        requiresRoleSelection,
        // switchRole n'est pas utilisé directement ici, on le retire
    } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const isPublic = isPublicRoute(location.pathname);

    // Route publique
    if (isPublic) {
        return (
            <Routes>
                {roleConfig && Object.values(roleConfig).flatMap(config =>
                    config.routes.map(route => (
                        <Route key={route.path} path={route.path} element={route.element} />
                    ))
                )}
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        );
    }

    // Chargement
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-900 mx-auto"></div>
                    <p className="text-sm text-slate-500 mt-4 font-serif">Chargement de votre session...</p>
                </div>
            </div>
        );
    }

    // Pas de token ou pas d'utilisateur
    if (!token || !user) {
        return <Navigate to="/login" replace />;
    }

    // Sélection de rôle requise
    if (requiresRoleSelection) {
        return (
            <RoleSwitcher
                mode="gate"
                onSwitched={(redirectPath) => {
                    navigate(redirectPath || '/dashboard', { replace: true });
                }}
            />
        );
    }

    // Aucun rôle actif (cas anormal)
    if (!activeRole) {
        console.error('[PROTECTEDROUTE] Aucun rôle actif alors que la sélection n’est pas requise');
        logout();
        return <Navigate to="/login" replace />;
    }

    const currentRoleKey = `ROLE_${activeRole.role}`;

    // Rôle non configuré
    if (!roleConfig || !roleConfig[currentRoleKey]) {
        console.error('[PROTECTEDROUTE] Rôle non configuré:', currentRoleKey);
        logout();
        return <Navigate to="/login" replace />;
    }

    const config = roleConfig[currentRoleKey];
    const Layout = config.layout || React.Fragment;

    return (
        <div className="animate-in fade-in duration-500">
            <Layout
                onLogout={logout}
                role={currentRoleKey}
                username={user?.email || user?.phone || 'Utilisateur'}
                {...(config.layoutProps || {})}
            >
                <Routes>
                    {config.routes.map((route, index) => (
                        <Route key={`${currentRoleKey}-${index}`} path={route.path} element={route.element} />
                    ))}
                    <Route path="*" element={<Navigate to={config.defaultPath} replace />} />
                </Routes>
            </Layout>
        </div>
    );
};

export default ProtectedRoute;